import { cn } from "@gettactic/helpers";
import React from "react";
import { Flex } from "../../primitives";
import { FormControlWrapper } from "../FormControlWrapper";
import { useFormControlWrapper } from "../FormControlWrapper/FormControlWrapperProvider";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import {
  SelectInputItem,
  SelectInputProps,
  SelectInputPropsWithFormControl
} from "./SelectInput.types";
import { UnifiedSelect, SelectItem } from "../UnifiedSelect";
import { ChevronDownIcon } from "lucide-react";

interface SelectItemChildProps {
  value?: string;
  children?: React.ReactNode;
  disabled?: boolean;
}

// Helper to extract items from children (for backward compatibility)
function extractItemsFromChildren(
  children: React.ReactNode
): SelectInputItem[] {
  const items: SelectInputItem[] = [];

  React.Children.forEach(children, (child) => {
    if (
      React.isValidElement<SelectItemChildProps>(child) &&
      child.props.value !== undefined
    ) {
      items.push({
        id: String(child.props.value),
        name: String(child.props.children ?? child.props.value),
        disabled: child.props.disabled
      });
    }
  });

  return items;
}

const SelectInputInput: React.FC<SelectInputProps> = (props) => {
  const {
    inputClassName,
    value: controlledValue,
    onChangeValue,
    className,
    children,
    placeholder,
    items: itemsProp,
    searchable = false,
    searchPlaceholder,
    contentWidth,
    disabled,
    ...rest
  } = props;
  const { id } = useFormControlWrapper();
  const [selectedValue, setSelectedValue] = React.useState<
    string | undefined
  >();

  // Convert children to items if items prop is not provided
  const items: SelectItem<unknown>[] = React.useMemo(() => {
    if (itemsProp) {
      return itemsProp.map((item) => ({
        id: item.id,
        name: item.name,
        keywords: item.keywords,
        icon: item.icon,
        disabled: item.disabled
      }));
    }
    return extractItemsFromChildren(children);
  }, [itemsProp, children]);

  const handleSelect = (item: SelectItem<unknown> | null) => {
    const newValue = item?.id ?? "";
    setSelectedValue(newValue || undefined);
    onChangeValue?.(newValue);
  };

  const value = controlledValue ?? selectedValue;

  // Find the selected item
  const selectedItem = React.useMemo(() => {
    if (!value) return null;
    return items.find((item) => item.id === value) ?? null;
  }, [items, value]);

  const triggerContent = (
    <span
      className={cn(
        "flex h-[42px] w-full min-w-0 items-center justify-between gap-1 cursor-pointer text-sm rounded-md border border-input bg-background px-3 py-2 hover:bg-accent/50",
        disabled && "opacity-50 cursor-not-allowed",
        inputClassName
      )}
    >
      <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {selectedItem ? (
          <span className="truncate">{selectedItem.name}</span>
        ) : (
          <span className="truncate text-muted-foreground">{placeholder}</span>
        )}
      </span>
      <ChevronDownIcon className={"size-4"} />
    </span>
  );

  return (
    <Flex {...rest} className={cn("w-full flex-row gap-2", className)} id={id}>
      <UnifiedSelect
        items={items}
        value={selectedItem}
        onSelect={handleSelect}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder}
        placeholder={placeholder}
        contentWidth={contentWidth || "var(--radix-popover-trigger-width)"}
        trigger={triggerContent}
        disabled={disabled}
      />
    </Flex>
  );
};

export const SelectInput = React.forwardRef<
  React.ComponentRef<typeof FormControlWrapper>,
  SelectInputPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <FormControlWrapper
      variant={"unstyled"}
      wrapperClassName={"py-0"}
      enableSlotsInputFocus
      ref={ref}
      {...wrapperProps}
    >
      <SelectInputInput {...inputProps} />
    </FormControlWrapper>
  );
});

SelectInput.displayName = "SelectInput";
